// Targets that already have an override row, whatever it holds: client rows, platform rows, and the number of
// platform rows of each client.
export const configuredTargets = (overrides) => {
    const clients = new Set();
    const platforms = new Set();
    const platformRowsByClient = new Map();
    overrides.forEach(({ client, platform }) => {
        if (platform) {
            platforms.add(platform.uuid);
            platformRowsByClient.set(client.uuid, (platformRowsByClient.get(client.uuid) ?? 0) + 1);
        } else {
            clients.add(client.uuid);
        }
    });
    return { clients, platforms, platformRowsByClient };
};

// Search results grouped by parent, in the API order (by name). A sub-client follows its parent, one level
// below, when the parent is among the results; otherwise it keeps its place and carries the parent's name.
export const groupByParent = (clients) => {
    const found = new Set(clients.map((client) => client.uuid));
    const isNested = (client) => Boolean(client.parent) && found.has(client.parent.uuid);
    return clients
        .filter((client) => !isNested(client))
        .flatMap((client) => [
            { client, level: 1, parentName: client.parent?.name ?? null },
            ...clients
                .filter((child) => isNested(child) && child.parent.uuid === client.uuid)
                .map((child) => ({ client: child, level: 2, parentName: null })),
        ]);
};

const normalize = (text) =>
    (text ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase();

// Splits a text around the occurrences of the term (case and accents ignored), to show them in bold. Each
// character is normalized on its own, so that every normalized character points back to the original one it
// comes from: the segments keep the original characters, and a combining accent stays with its letter.
export const highlightSegments = (text, term) => {
    const source = text ?? '';
    const needle = normalize(term);
    if (!needle) return [{ text: source, match: false }];

    let normalized = '';
    const origins = [];
    let index = 0;
    for (const character of source) {
        const folded = normalize(character);
        normalized += folded;
        for (let i = 0; i < folded.length; i += 1) origins.push(index);
        index += character.length;
    }
    const originOf = (position) => (position < origins.length ? origins[position] : source.length);

    const segments = [];
    let cursor = 0;
    let found = normalized.indexOf(needle);
    while (found !== -1) {
        const start = originOf(found);
        const end = originOf(found + needle.length);
        if (start > cursor) segments.push({ text: source.slice(cursor, start), match: false });
        segments.push({ text: source.slice(start, end), match: true });
        cursor = end;
        found = normalized.indexOf(needle, found + needle.length);
    }
    if (cursor < source.length || segments.length === 0) segments.push({ text: source.slice(cursor), match: false });
    return segments;
};
